import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaCheckCircle, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import "./Admissions.css";
import { doc, getDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { forceDownloadUrl } from "../utils/uploadFile";

// Agar Firestore mein koi fee nahi hai to yeh default list dikhegi
const DEFAULT_FEES = [
  { id: "d1", className: "Playgroup", admissionFee: 5000, monthlyFee: 2000 },
  { id: "d2", className: "Nursery", admissionFee: 5000, monthlyFee: 2000 },
  { id: "d3", className: "Prep", admissionFee: 5000, monthlyFee: 2000 },
  { id: "d4", className: "One", admissionFee: 5000, monthlyFee: 2500 },
  { id: "d5", className: "Two", admissionFee: 5000, monthlyFee: 2500 },
  { id: "d6", className: "Three", admissionFee: 5000, monthlyFee: 2500 },
  { id: "d7", className: "Four", admissionFee: 5000, monthlyFee: 2500 },
  { id: "d8", className: "Five", admissionFee: 5000, monthlyFee: 2500 },
  { id: "d9", className: "Six", admissionFee: 5000, monthlyFee: 3000 },
  { id: "d10", className: "Seven", admissionFee: 5000, monthlyFee: 3000 },
  { id: "d11", className: "Eight", admissionFee: 5000, monthlyFee: 3000 },
  { id: "d12", className: "Nine", admissionFee: 5000, monthlyFee: 3000 },
  { id: "d13", className: "Tenth", admissionFee: 5000, monthlyFee: 3000 },
  { id: "d14", className: "1st & 2nd Year", admissionFee: 5000, monthlyFee: 5000 },
];

const SIDEBAR_ITEMS = [
  { id: "requirements", label: "Admission Requirements" },
  { id: "tests", label: "Admission Tests" },
  { id: "criteria", label: "Class Levels & Age Criteria" },
  { id: "fees", label: "Fee Structure" },
  { id: "download", label: "Download Form" },
  { id: "contact", label: "Contact Office" },
  { id: "faq", label: "FAQs" },
];

const MOBILE_BP = 992;
const PILL_BAR_H = 70;

const formatFee = (v) => {
  const n = Number(v);
  return Number.isNaN(n) ? v : n.toLocaleString("en-US");
};

function Admissions() {
  const [activeSection, setActiveSection] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [admissionsOpen, setAdmissionsOpen] = useState(true);
  const [fees, setFees] = useState([]);
  const [feesLoaded, setFeesLoaded] = useState(false);
  const [admForm, setAdmForm] = useState(null);
  const navigate = useNavigate();

  // Click ke baad scroll-spy ko kuch der ke liye us section par lock rakhte hain
  const lockRef = useRef(null); // { id, timer }

  // Admission open/closed status
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "admissions"));
        if (!cancelled && snap.exists()) setAdmissionsOpen(!!snap.data().isOpen);
      } catch (err) {
        console.error("Admission status error:", err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fee structure (realtime)
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "fees"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setFees(list);
        setFeesLoaded(true);
      },
      (error) => {
        console.error("Fees load error:", error.message);
        setFeesLoaded(true);
      }
    );
    return () => unsub();
  }, []);

  // Admission form (admin panel se upload hota hai)
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "settings", "admissionForm"),
      (snap) => setAdmForm(snap.exists() ? snap.data() : null),
      (error) => console.error("Admission form load error:", error.message)
    );
    return () => unsub();
  }, []);

  // Navbar ki actual height measure karke sidebar ko usi ke neeche rakho
  useEffect(() => {
    const layout = document.querySelector(".admissions-layout");
    const nav =
      document.querySelector(".navbar") ||
      document.querySelector("header") ||
      document.querySelector("nav");
    if (!layout || !nav) return;

    const setOffset = () => {
      const h = Math.round(nav.getBoundingClientRect().height);
      layout.style.setProperty("--site-nav-offset", `${h}px`);
    };

    setOffset();
    const ro = new ResizeObserver(setOffset);
    ro.observe(nav);
    window.addEventListener("resize", setOffset);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setOffset);
    };
  }, []);

  const formHref = admForm?.fileUrl
    ? forceDownloadUrl(admForm.fileUrl)
    : "/files/admission-forms.pdf";

  const feeRows = fees.length > 0 ? fees : feesLoaded ? DEFAULT_FEES : [];

  // Scroll spy + progress (rAF se throttle)
  useEffect(() => {
    let ticking = false;

    const update = () => {
      ticking = false;

      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);

      // Click ke baad programmatic scroll chal raha hai: highlight wahi rahe
      if (lockRef.current) {
        setActiveSection(lockRef.current.id);
        return;
      }

      // Page ke end par aakhri section active
      if (docHeight > 0 && window.scrollY >= docHeight - 4) {
        setActiveSection(SIDEBAR_ITEMS[SIDEBAR_ITEMS.length - 1].id);
        return;
      }

      const layout = document.querySelector(".admissions-layout");
      const navH =
        parseFloat(getComputedStyle(layout).getPropertyValue("--site-nav-offset")) || 84;
      const pillH = window.innerWidth <= MOBILE_BP ? PILL_BAR_H : 0;
      const line = navH + pillH + 24;

      let current = "";
      SIDEBAR_ITEMS.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - line <= 0) current = id;
      });
      setActiveSection(current);
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Scroll lock ko unlock karne ke liye (scrollend ya timeout)
  useEffect(() => {
    const unlock = () => {
      if (lockRef.current) {
        clearTimeout(lockRef.current.timer);
        lockRef.current = null;
      }
    };
    window.addEventListener("scrollend", unlock);
    return () => {
      window.removeEventListener("scrollend", unlock);
      unlock();
    };
  }, []);

  // Mobile pill bar: active pill sirf apne andar scroll ho (page nahi hilta)
  useEffect(() => {
    const aside = document.querySelector(".admissions-sidebar");
    const list = aside?.querySelector("ul");
    const active = aside?.querySelector("a.active");
    if (list && active && window.innerWidth <= MOBILE_BP) {
      const left = active.offsetLeft - list.clientWidth / 2 + active.offsetWidth / 2;
      list.scrollTo({ left: Math.max(left, 0), behavior: "smooth" });
    }
  }, [activeSection]);

  // Link click: turant active, sahi section tak scroll
  const scrollToSection = useCallback((id) => {
    const section = document.getElementById(id);
    if (!section) return;

    const isSmall = window.innerWidth <= MOBILE_BP;

    // 1) Highlight turant badlo
    setActiveSection(id);

    // 2) Scroll ke dauran spy ko lock karo
    if (lockRef.current) clearTimeout(lockRef.current.timer);
    lockRef.current = {
      id,
      timer: setTimeout(() => {
        lockRef.current = null;
      }, isSmall ? 500 : 1500),
    };

    // 3) Section ka reveal transition band karke turant visible karo,
    //    taake position sahi measure ho
    section.style.transition = "none";
    section.classList.add("visible");
    requestAnimationFrame(() => {
      section.style.transition = "";
    });

    // 4) Scroll (nav aur pill bar ka offset CSS scroll-margin-top se aata hai)
    section.scrollIntoView({
      behavior: isSmall ? "auto" : "smooth",
      block: "start",
    });
  }, []);

  // Section fade-in animation
  useEffect(() => {
    const sections = document.querySelectorAll(".admissions-section");
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        }),
      { threshold: 0.2 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="admissions-layout">
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* Sidebar */}
      <aside className="admissions-sidebar">
        <h3>Admissions</h3>
        <ul>
          {SIDEBAR_ITEMS.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className={activeSection === item.id ? "active" : ""}
                aria-current={activeSection === item.id ? "true" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                  window.history.replaceState(null, "", `#${item.id}`);
                }}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main content */}
      <div className="admissions-content">
        <div className="admissions-title">
          <h1>Admissions</h1>
          <p>Start your journey with us – explore requirements, process, and criteria for joining our school.</p>
        </div>

        {admissionsOpen ? (
          <button type="button" className="Apply-btn" onClick={() => navigate("/applyform")}>
            Apply Now
          </button>
        ) : (
          <button type="button" className="admission-btn disabled" disabled>
            Admissions Closed
          </button>
        )}

        <section id="requirements" className="admissions-section card">
          <h2>Admission Requirements</h2>
          <ul className="requirement-list">
            <li><FaCheckCircle className="icon" /> Submit form, pay fee, and receive payment documents.</li>
            <li><FaCheckCircle className="icon" /> Submit 2 photos, Birth Certificate; transfers add last report & character certificates.</li>
            <li><FaCheckCircle className="icon" /> Take the admission test (except Pre-school) and attend parent interview.</li>
            <li><FaCheckCircle className="icon" /> Results are shared within a week.</li>
            <li><FaCheckCircle className="icon" /> Parents receive admission fee challan with payment instructions.</li>
          </ul>
        </section>

        <section id="tests" className="admissions-section card">
          <h2>Admission Tests</h2>
          <p>No written test for Pre-school. For Primary & Secondary, tests are based on the following:</p>
          <div className="test-boxes">
            <div className="test-box red"><p><strong>KG & I</strong></p><p>English, Urdu, Mathematics & General Knowledge (oral)</p></div>
            <div className="test-box blue"><p><strong>Class II – VII</strong></p><p>English, Urdu, Mathematics & Science</p></div>
          </div>
        </section>

        <section id="criteria" className="admissions-section card">
          <h2>Class Levels & Age Criteria</h2>
          <div className="table-scroll">
            <table className="class-table">
              <thead>
                <tr>
                  <th className="col-class">Class Level</th>
                  <th className="col-age">Age</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="col-class">Playgroup</td><td className="col-age">4+</td></tr>
                <tr><td className="col-class">Nursery</td><td className="col-age">5+</td></tr>
                <tr><td className="col-class">Prep</td><td className="col-age">6+</td></tr>
                <tr><td className="col-class">One</td><td className="col-age">7+</td></tr>
                <tr><td className="col-class">Two</td><td className="col-age">8+</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="fees" className="admissions-section card">
          <h2>Fee Structure</h2>
          <p className="fee-caption">All amounts are in Pakistani Rupees (PKR).</p>
          <div className="table-scroll">
            <table className="class-table fee-table">
              <thead>
                <tr>
                  <th className="col-class">Class</th>
                  <th className="col-fee">
                    <span className="th-long">Admission Fee (PKR)</span>
                    <span className="th-short">Admission</span>
                  </th>
                  <th className="col-fee">
                    <span className="th-long">Monthly Fee (PKR)</span>
                    <span className="th-short">Monthly</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {feeRows.map((f) => (
                  <tr key={f.id}>
                    <td className="col-class">{f.className}</td>
                    <td className="col-fee">{formatFee(f.admissionFee)}</td>
                    <td className="col-fee">{formatFee(f.monthlyFee)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note">* Fees may vary by campus. Contact admissions office for updates.</p>
        </section>

        <section id="download" className="admissions-section card center-text">
          <h2>Download Admission Form</h2>
          <p>Please download the admission form and complete it carefully before submission.</p>
          <a href={formHref} download target="_blank" rel="noopener noreferrer" className="download-btn">
            📄 Download Admission Form
          </a>
        </section>

        <section id="contact" className="admissions-section card">
          <h2>Contact Office</h2>
          <p>Please reach out to our Admissions Office:</p>
          <div className="adm-contact-list">
            <a className="adm-contact-item" href="tel:+923047630462">
              <span className="adm-contact-icon"><FaPhoneAlt /></span>
              <span className="adm-contact-text">
                <small>Call us</small>
                <strong>+92 304-7630462</strong>
              </span>
            </a>
            <a className="adm-contact-item" href="mailto:academicgroupofschools@gmail.com">
              <span className="adm-contact-icon"><FaEnvelope /></span>
              <span className="adm-contact-text">
                <small>Email us</small>
                <strong>academicgroupofschools@gmail.com</strong>
              </span>
            </a>
          </div>
        </section>

        <section id="faq" className="admissions-section card">
          <h2>FAQs</h2>
          <ul className="faq-list">
            <li><strong>Q:</strong> What documents are needed for admission?<br /><strong>A:</strong> Birth certificate, photos, last school report.</li>
            <li><strong>Q:</strong> How long does the process take?<br /><strong>A:</strong> Usually 1–2 weeks after the test.</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default Admissions;